import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';

import { BOROUGHS } from 'utils/boroughs';
import { fetchProperty } from 'reducers/propertyReducer';
import { searchAddressesByBlockAndLot, clearBlockAndLotSearch } from 'reducers/blockAndLotSearchReducer';

import "./BlockAndLotSearch.css";

function BlockAndLotSearch(props) {
	const dispatch = useDispatch();
	const [block, setBlock] = useState('');
	const [lot, setLot] = useState('');
	const [borough, setBorough] = useState(BOROUGHS.MANHATTAN);
	const [showAddressList, setShowAddressList] = useState(false);
	const dropdownRef = useRef(null);

	// Get state from Redux
	const { addresses, isLoading, hasError, error } = useSelector(
		(state) => state.blockAndLotSearch
	);

	let { label, hideLabel } = props;
	label = label || 'Block and Lot Search';

	const searchAddresses = () => {
		if (!block || !lot || !borough) {
			return;
		}

		setShowAddressList(true); // Show dropdown with loader
		dispatch(searchAddressesByBlockAndLot(block, lot, borough));
	};

	const onBlockLotSearch = () => {
		searchAddresses();
	}

	const onAddressSelect = (selectedAddress) => {
		setShowAddressList(false);
		dispatch(clearBlockAndLotSearch());
		if (props.onSelect) {
			props.onSelect({ address: selectedAddress.address, block, lot, borough });
		}
		// Navigate to property overview
		dispatch(fetchProperty(selectedAddress.address));
		dispatch(push(`/property/${encodeURIComponent(selectedAddress.address)}`));
	}

	// Handle search results
	useEffect(() => {
		if (!isLoading && showAddressList) {
			if (addresses.length === 0 && !hasError) {
				// If no addresses found, fall back to original behavior
				setShowAddressList(false);
				if (props.onSelect) {
					props.onSelect({ block, lot, borough });
				}
			} else if (hasError) {
				// Handle error case
				setShowAddressList(false);
				console.error('Error searching addresses:', error);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, addresses.length, hasError, error, showAddressList, block, lot, borough]);

	const onEnter = (e) => {
		if (e.key === 'Enter') {
			onBlockLotSearch();
		}
	}

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setShowAddressList(false);
			}
		};

		if (showAddressList) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showAddressList]);

	return (
		<div className="block-and-lot-search" ref={dropdownRef}>
			{!hideLabel && <label className="form-label">{label}</label>}
			<div className="input-group block-and-lot">
				<input
					type="text"
					className="form-control block-input"
					placeholder="Block"
					onChange={(e) => setBlock(e.target.value)}
					onKeyPress={(e) => onEnter(e)}
					value={block}
				/>
				<input
					type="text"
					className="form-control lot-input"
					placeholder="Lot"
					onChange={(e) => setLot(e.target.value)}
					onKeyPress={(e) => onEnter(e)}
					value={lot}
				/>
				<select className="form-select borough-select" placeholder="Borough" value={borough} onChange={e => setBorough(e.target.value)}>
					<option value={BOROUGHS.MANHATTAN}>Manhattan</option>
					<option value={BOROUGHS.BRONX}>Bronx</option>
					<option value={BOROUGHS.BROOKLYN}>Brooklyn</option>
					<option value={BOROUGHS.QUEENS}>Queens</option>
					<option value={BOROUGHS.STATEN_ISLAND}>Staten Island</option>
				</select>
				<button
					className="btn btn-outline-secondary"
					type="button"
					onClick={onBlockLotSearch}
					disabled={isLoading}
				>
					{isLoading ? (
						<i className="bi bi-hourglass-split" />
					) : (
						<i className="bi bi-search" />
					)}
				</button>
			</div>
			{showAddressList && (
				<div className="address-list-dropdown">
					{isLoading ? (
						<div className="address-list-loading">
							<div className="spinner-border spinner-border-sm text-primary" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
							<span className="loading-text">Loading addresses...</span>
						</div>
					) : addresses.length > 0 ? (
						<ul className="list-group">
							{addresses.map((addr, index) => (
								<li
									key={index}
									className="list-group-item address-list-item"
									onClick={() => onAddressSelect(addr)}
									style={{ cursor: 'pointer' }}
								>
									{addr.address}
								</li>
							))}
						</ul>
					) : (
						<div className="address-list-empty">
							<span className="text-muted">No addresses found</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

export default BlockAndLotSearch;
